import { Body, Container, Heading, useToast } from '@hover/blueprint';
import React, { useState } from 'react';

import interiorMeasurements from 'src/assets/img/homeownerApp/modules/linkOuts/interiorMeasurements.jpg';
import scanPromoExterior from 'src/assets/img/homeownerApp/modules/linkOuts/scanPromoExterior.png';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { CARD_PADDING, SplitCard } from '../../Card';

import type { LinkoutType } from './hooks';

export const ScanForMeasurements = ({
  projectId,
  config,
  isExteriorProject,
}: {
  projectId: string;
  config: LinkoutType;
  isExteriorProject: boolean;
}) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const toast = useToast();
  const [notifyMe, setNotifyMe] = useState(false);

  const { trackingData } = useHomeownerAppContext();

  const backgroundImage = isExteriorProject
    ? scanPromoExterior
    : interiorMeasurements;

  const handleNotifyMeClick = () => {
    setNotifyMe(true);
    toast({
      position: 'top-right',
      duration: null,
      description: t('toast.comingSoon'),
      props: {
        colorScheme: 'dark',
        borderRadius: '400',
      },
    });
  };

  const { subtitle, title, description, buttonText } = config;

  return (
    <Container mt={600} padding={0} width="100%">
      <SplitCard
        backgroundImage={backgroundImage}
        data-testid="scan-for-measurements"
        descriptionBoxProps={{
          alignItems: 'center',
          backgroundColor: 'neutral.50',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: CARD_PADDING,
          py: { tablet: 500, desktop: 1000 },
          width: { base: '100%', desktop: '50%' },
        }}
        imageBoxProps={{
          bgColor: 'neutral.50',
          bgSize: { base: 'cover' },
          minH: { base: '218px' },
          maxH: { base: 750, desktop: 'unset' },
          sx: { aspectRatio: { base: '4/2', desktop: 'auto' } },
          width: { base: '100%', desktop: '50%' },
        }}
      >
        <Body isSemiBold m={0} pb={400} pt={0} size={300}>
          {subtitle}
        </Body>
        <Heading
          as="p"
          m={0}
          pt={0}
          size={{ base: 500, tablet: 600 }}
          textAlign="center"
        >
          {title}
        </Heading>
        <Body
          opacity={0.64}
          size={{ base: 300, tablet: 400 }}
          textAlign="center"
        >
          {description}
        </Body>
        <Button
          includeTrackingImpression
          mt={400}
          onClick={notifyMe ? () => {} : handleNotifyMeClick}
          optionalTrackingProperties={{
            track_id: 'measurements-get-notified',
            button_location: 'Measurements tile',
            primary_cta: true,
            project_id: Number(projectId),
            ...trackingData,
          }}
          size="large"
          variant={notifyMe ? 'secondary' : 'primary'}
        >
          {buttonText}
        </Button>
      </SplitCard>
    </Container>
  );
};
